import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { errorToast, successToast } from '@/helpers';
import { _isEmpty, _isNotEmpty, _noop, _notNil } from '@/littledash';

import type { ID } from '@/model/Common.model';
import InVivoError from '@/model/InVivoError';
import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import { standardSections } from '@/referenceData/study/type';
import { ApiService } from '@/support/ApiService';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import { DragHintBanner } from '@/components/UI/Banner/Reusable';
import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import DraggableList, { asSortableListItem } from '@/components/UI/List/DraggableList';
import SearchSelect from '@/components/UI/SearchSelect';

interface SectionMetadata extends MetadataField {
  required: boolean;
}

interface SectionData {
  id?: ID;
  name: string;
  metadata: Array<{ info: MetadataField; required: boolean }>;
}

interface ShowProps {
  formId: ID;
  sectionId?: ID;
  onSaveSection: () => void;
  saveTemplate: () => Promise<void>;
  customSectionNames: Array<string>;
}

const Show: FC<ShowProps> = ({ formId, sectionId, onSaveSection, saveTemplate, customSectionNames }) => {
  const [section, setSection] = useState<SectionData | undefined>(undefined);
  const [studyMetadata, setStudyMetadata] = useState<Array<MetadataField> | undefined>(undefined);
  const [assignedStudyMetadataIds, setAssignedStudyMetadataIds] = useState<Array<ID> | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<unknown>(null);

  const fetchSectionAndMetadata = useCallback(async (fId: ID, sId?: ID) => {
    setLoading(true);
    try {
      const response = await ApiService.call({
        endpoint: 'GET /api/v1/metadata-glossary',
        query: { filter_type: MetadataTypes.study_meta },
        options: {
          onError: {
            slug: 'study-metadata-fetch',
            toast: false,
            capture: true,
            throw: true,
          },
        },
      });
      const data = response?.body ?? [];
      setStudyMetadata(data as Array<MetadataField>);
      const {
        data: { metadata_ids },
      } = await Http.get(apiRoute('team.studies.forms.metadata', { id: fId }));
      setAssignedStudyMetadataIds(metadata_ids);
      if (_notNil(sId)) {
        const sectionResponse = await Http.get(
          apiRoute('team.studies.forms.section.show', {
            formId: fId,
            sectionId: sId,
          })
        );
        setSection(sectionResponse.data.data);
      }
    } catch (error) {
      setApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectionAndMetadata(formId, sectionId);
  }, [formId, sectionId, fetchSectionAndMetadata]);

  return (
    <div className="pa2 bg-light-gray ba b--moon-gray br2 shadow-5 mw7 center">
      {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
      {loading ? (
        <Loading />
      ) : (
        <SectionForm
          formId={formId}
          section={section}
          studyMetadata={studyMetadata ?? []}
          assignedStudyMetadataIds={assignedStudyMetadataIds ?? []}
          onSaveSection={onSaveSection}
          saveTemplate={saveTemplate}
          customSectionNames={customSectionNames}
        />
      )}
    </div>
  );
};

interface SectionFormProps {
  formId: ID;
  section?: SectionData;
  studyMetadata: Array<MetadataField>;
  assignedStudyMetadataIds: Array<ID>;
  onSaveSection: () => void;
  saveTemplate: () => Promise<void>;
  customSectionNames: Array<string>;
}

interface SectionFormData {
  name: string;
  metadata: Array<{ value: SectionMetadata }>;
}

export const SectionForm: FC<SectionFormProps> = ({
  formId,
  section,
  studyMetadata,
  assignedStudyMetadataIds = [],
  onSaveSection = _noop,
  saveTemplate,
  customSectionNames = [],
}) => {
  const isEditing = _notNil(section) && !_isEmpty(section);
  const defaultSection = {
    name: section?.name ?? '',
    metadata: section?.metadata
      ? section.metadata.reduce<Array<SectionMetadata>>((acc, m) => {
          const { info, ...rest } = m;
          acc.push({ ...info, ...rest } as SectionMetadata);
          return acc;
        }, [])
      : [],
  };

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Array<ID>>(defaultSection.metadata.map((s) => s.id));

  const formMethods = useForm<SectionFormData>({
    defaultValues: {
      name: defaultSection.name,
      metadata: defaultSection.metadata.map((meta) => ({ value: { ...meta } })),
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
    control,
    setError,
    clearErrors,
  } = formMethods;
  const dispatch = useDispatch();
  const { fields, move, append, remove } = useFieldArray({
    control,
    name: 'metadata',
  });

  const handleReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      move(sourceIndex, destinationIndex);
    },
    [move]
  );

  const handleSelect = useCallback(
    ({ type, value }: { type: 'ADDED' | 'REMOVED'; value: ID }) => {
      if (type === 'ADDED') {
        const metadata = studyMetadata.find((meta) => Number(meta.id) === Number(value));
        if (_notNil(metadata)) {
          append({
            value: {
              ...metadata,
              required: false,
            },
          });
        }
      } else {
        const index = fields.findIndex((field) => Number(field.value.id) === Number(value));
        if (index !== -1) {
          remove(index);
        }
      }
    },
    [studyMetadata, fields, append, remove]
  );

  const selectableMetadata = studyMetadata.reduce<Array<MetadataField>>((acc, meta) => {
    if (isEditing && _notNil(section)) {
      const sectionMetadataIds = section.metadata.map((m) => m.info.id);
      if (sectionMetadataIds.includes(meta.id)) {
        acc.push(meta);
        return acc;
      }
    }
    if (!assignedStudyMetadataIds.includes(meta.id)) {
      acc.push(meta);
    }
    return acc;
  }, []);

  const handleSelectAll = useCallback(() => {
    remove(fields.map((_: unknown, i: number) => i));
    append(
      selectableMetadata.map((meta) => ({
        value: { ...meta, required: false },
      }))
    );
  }, [fields, remove, append, selectableMetadata]);

  const handleClearSelection = useCallback(() => {
    remove(fields.map((_: unknown, i: number) => i));
  }, [fields, remove]);

  const closeModal = useCallback(() => {
    dispatch({ type: 'CLOSE_MODAL' });
  }, [dispatch]);

  useEffect(() => {
    if (isSubmitting || isSubmitted) {
      clearErrors('metadata');
      if (_isEmpty(fields)) {
        setError('metadata', {
          type: 'manual',
          message: 'Sections must contain at least one item.',
        });
      }
    }
  }, [isSubmitting, isSubmitted, setError, clearErrors, selected, fields]);

  const onSubmit = async (data: SectionFormData) => {
    if (_isNotEmpty(data.metadata)) {
      setSubmitting(true);
      try {
        await saveTemplate();
        if (isEditing) {
          if (!_notNil(section) || !_notNil(section.id)) {
            const err = new InVivoError('Cannot update study form section: missing section id', {
              slug: 'section-form-update',
            });
            setApiError(err);
            errorToast('There was a problem with your submission.');
            ExceptionHandler.captureException(err);
            return;
          }
        }
        const method = isEditing ? 'put' : 'post';
        const url = isEditing
          ? apiRoute('team.studies.forms.section.update', {
              formId,
              sectionId: section.id,
            })
          : apiRoute('team.studies.forms.section.create', { formId });
        await Http[method](url, {
          ...data,
          metadata: data.metadata.map((meta) => ({ ...meta.value })),
        });
        closeModal();
        successToast('Section saved.');
        onSaveSection();
      } catch (error) {
        setApiError(error);
        errorToast('There was a problem with your submission.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <>
      {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
      <h3 className="f4 lh-title normal pa3">{isEditing ? `Editing ${section?.name}` : 'Add section'}</h3>
      <div className="ph3 pb3">
        <DragHintBanner />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-50 pa3">
          <label htmlFor="section-name">Name</label>
          <input
            id="section-name"
            type="text"
            autoFocus={!isEditing}
            className={cn({ input__error: _notNil(errors?.name) })}
            {...register('name', {
              required: 'This field is required',
              minLength: {
                value: 3,
                message: 'Minimum length of 3 characters',
              },
              maxLength: {
                value: 50,
                message: 'Maximum length of 50 characters',
              },
              validate: {
                notReserved: (val: string) =>
                  !Object.values(standardSections).includes(val) ||
                  'This name is reserved for Benchling In Vivo sections',
                doesntExistAlready: (val: string) =>
                  !(customSectionNames.filter((name) => name !== section?.name).includes(val) ?? false) ||
                  'Section names must be unique',
              },
            })}
            style={{ marginBottom: 0 }}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
        <div className="flex flex-wrap justify-between">
          <div className="w-50 pa3">
            <SearchSelect
              className="ui-card"
              sections={[
                {
                  title: 'Study metadata',
                  items: selectableMetadata,
                },
              ]}
              selected={selected}
              setSelected={setSelected}
              onSelect={handleSelect}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
            />
            <ErrorMessage
              errors={errors}
              name="metadata"
              render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
            />
          </div>
          <div className="w-50 pa3">
            <div className="ui-card pa3 overflow-auto" style={{ height: 409 }}>
              {_isNotEmpty(fields) ? (
                <FormProvider {...formMethods}>
                  <DraggableList
                    canAmend={false}
                    data={fields}
                    sortable={{
                      droppableId: 'study-section-metadata-item',
                      onReorder: handleReorder,
                      disabled: fields.length <= 1,
                    }}
                    renderItem={asSortableListItem({
                      canSort: fields.length > 1,
                      itemClassName: 'bb b--moon-gray pb3 mb3',
                    })(MetadataFormItem)}
                  />
                </FormProvider>
              ) : (
                <div className="bg-light-silver pa3 br2 w-100 h-100 items-center flex">
                  <h3 className="lh-title f5 w-100 tc">Select metadata for this section</h3>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="pa3">
          <Button submit className="mr3" disabled={submitting}>
            Save
          </Button>
          <Button plain onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
};

interface MetadataFormItemProps {
  id: string;
  value: SectionMetadata;
  index: number;
}

const MetadataFormItem: FC<MetadataFormItemProps> = ({ value, index }) => {
  const { control } = useFormContext<SectionFormData>();

  return (
    <Controller
      key={index}
      defaultValue={value}
      name={`metadata.${index}.value`}
      control={control}
      render={({ field: { onChange, value: fieldValue } }) => (
        <div>
          <h3 className="f6 normal lh-title pb2">{fieldValue.title}</h3>
          <Checkbox
            className="pointer"
            label="Required"
            name={String(fieldValue.id)}
            checked={fieldValue.required === true}
            onChange={() =>
              onChange({
                ...fieldValue,
                required: !fieldValue.required,
              })
            }
          />
        </div>
      )}
    />
  );
};

export default Show;
